/* LUCA animations — vanilla JS port of the design-file React animations.
   - "hero": cinematic multi-watch reveal (homepage, ~17s loop)
   - "explainer": CH-1 anatomy guided tour (product page, ~65s loop)
   Containers declare data-luca-anim="hero|explainer" and carry a JSON config
   script tag with asset URLs. Everything is lazy-built on approach. */
(function () {
  'use strict';

  /* ── math helpers (identical to the design source) ─────────────────── */
  var E = {
    outQuad: function (x) { return 1 - (1 - x) * (1 - x); },
    inQuad: function (x) { return x * x; },
    outCubic: function (x) { return 1 - Math.pow(1 - x, 3); },
    outQuart: function (x) { return 1 - Math.pow(1 - x, 4); },
    inOutQuad: function (x) { return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2; },
    inOutCubic: function (x) { return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; },
    outBack: function (x) { var c1 = 1.70158, c3 = c1 + 1; return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2); }
  };
  function lerp(a, b, t) { return a + (b - a) * t; }
  function ramp(t, s, e, ease) {
    if (t <= s) return 0; if (t >= e) return 1;
    return (ease || E.outCubic)((t - s) / (e - s));
  }
  function loopFade(t, dur, fin, fout) {
    fin = fin || 0.5; fout = fout || 0.6;
    return Math.min(ramp(t, 0, fin, E.outQuad), 1 - ramp(t, dur - fout, dur, E.inQuad));
  }
  function smooth(x) { x = Math.max(0, Math.min(1, x)); return x * x * (3 - 2 * x); }

  var RED = '#E8112D';
  var DISP = "'Saira Condensed','Arial Narrow',sans-serif";
  var UI = "'Saira',system-ui,sans-serif";

  /* ── tiny DOM helpers ──────────────────────────────────────────────── */
  function el(tag, styles, parent) {
    var n = document.createElement(tag);
    if (styles) for (var k in styles) n.style[k] = styles[k];
    if (parent) parent.appendChild(n);
    return n;
  }
  function img(src, styles, parent) {
    var n = el('img', styles, parent);
    n.src = src; n.draggable = false; n.alt = '';
    return n;
  }
  var SVGNS = 'http://www.w3.org/2000/svg';
  function svgEl(tag, attrs, parent) {
    var n = document.createElementNS(SVGNS, tag);
    if (attrs) for (var k in attrs) n.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(n);
    return n;
  }

  /* ── stage: fixed design size, scaled to fit its container ─────────── */
  function Stage(container, W, H, DUR) {
    var stage = el('div', {
      position: 'absolute', left: '0', top: '0',
      width: W + 'px', height: H + 'px',
      transformOrigin: '0 0', overflow: 'hidden'
    }, container);
    container.style.position = container.style.position || 'relative';
    function fit() {
      var cw = container.clientWidth, ch = container.clientHeight;
      if (!cw || !ch) return;
      var s = Math.min(cw / W, ch / H);
      var ox = (cw - W * s) / 2, oy = (ch - H * s) / 2;
      stage.style.transform = 'translate(' + ox + 'px,' + oy + 'px) scale(' + s + ')';
    }
    fit();
    if (window.ResizeObserver) new ResizeObserver(fit).observe(container);
    else window.addEventListener('resize', fit);

    var updaters = [];
    var api = {
      root: stage, W: W, H: H, DUR: DUR,
      on: function (fn) { updaters.push(fn); },
      renderAt: function (t) { for (var i = 0; i < updaters.length; i++) updaters[i](t); }
    };

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    api.start = function (posterT) {
      if (reduce) { api.renderAt(posterT || 0); return; }
      var t = 0, last = null, running = true;
      function frame(now) {
        if (last == null) last = now;
        var dt = Math.min((now - last) / 1000, 0.1);
        last = now;
        if (running) {
          t = (t + dt) % DUR;
          api.renderAt(t);
        }
        requestAnimationFrame(frame);
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          running = entries[0].isIntersecting;
          if (running) last = null;
        }).observe(container);
      }
      requestAnimationFrame(frame);
    };
    return api;
  }

  /* visibility helper: fully hide a layer outside its time range.
     IMPORTANT: restore the node's own display value (flex for the centered
     cards) — assigning '' here was erasing display:flex and collapsing the
     brand/spec cards into left-stacked blocks. */
  function rangeLayer(node, t0, t1, pad) {
    pad = pad == null ? 0.6 : pad;
    var baseDisplay = node.style.display || 'block';
    return function (t) {
      var live = t >= t0 - pad && t <= t1 + pad;
      node.style.display = live ? baseDisplay : 'none';
      return live;
    };
  }

  /* ── shared: brand intro/outro card ────────────────────────────────── */
  function brandCard(S, cfg) {
    // cfg: {t0,t1,mode,logo,K, big (explainer headline|null), tagline, outroText}
    var K = cfg.K;
    var wrap = el('div', {
      position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', zIndex: '6', display: 'flex',
      flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
    }, S.root);
    var logo = img(cfg.logo, { width: (cfg.logoW * K) + 'px' }, wrap);
    var big = null;
    if (cfg.big) {
      big = el('div', {
        fontFamily: DISP, fontWeight: '800', fontSize: (116 * K) + 'px', color: '#fff',
        letterSpacing: '0.02em', textTransform: 'uppercase', marginTop: (34 * K) + 'px', lineHeight: '0.9'
      }, wrap);
      big.textContent = cfg.big;
    }
    var divider = el('div', {
      height: (3 * K) + 'px', background: RED, borderRadius: '2px',
      margin: (cfg.big ? 24 * K : 26 * K) + 'px 0 ' + (cfg.big ? 16 * K : 18 * K) + 'px'
    }, wrap);
    var tag = el('div', {
      fontFamily: UI, fontWeight: '600', fontSize: ((cfg.big ? 16 : 17) * K) + 'px',
      letterSpacing: (cfg.big ? '0.4em' : '0.42em'), color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase'
    }, wrap);
    if (cfg.outroText) {
      tag.style.color = '#fff'; tag.style.fontWeight = '700';
      tag.innerHTML = cfg.outroText + ' <span style="color:' + RED + '">→</span>';
    } else {
      tag.textContent = cfg.tagline;
    }
    var vis = rangeLayer(wrap, cfg.t0, cfg.t1, 0.3);
    S.on(function (t) {
      if (!vis(t)) return;
      var L = t - cfg.t0, dur = cfg.t1 - cfg.t0;
      wrap.style.opacity = Math.min(ramp(L, 0, cfg.big ? 0.5 : 0.45), 1 - ramp(L, dur - 0.5, dur, E.inQuad));
      var s = cfg.mode === 'intro' ? lerp(0.86, 1.0, ramp(L, 0, 0.7, E.outCubic)) : lerp(0.92, 1.0, ramp(L, 0, dur, E.outQuad));
      logo.style.transform = 'scale(' + s + ')';
      divider.style.width = lerp(0, 120 * K, ramp(L, cfg.big ? 0.5 : 0.3, cfg.big ? 1.1 : 0.9)) + 'px';
      if (big) {
        var bIn = ramp(L, 0.35, 1.0);
        big.style.opacity = bIn;
        big.style.transform = 'translateY(' + lerp(20 * K, 0, ramp(L, 0.35, 1.0, E.outQuart)) + 'px)';
      }
      tag.style.opacity = ramp(L, cfg.big ? 0.6 : (cfg.mode === 'intro' ? 0.5 : 0.4), cfg.big ? 1.2 : (cfg.mode === 'intro' ? 1.1 : 1.0));
    });
  }

  /* ══════════════════════════════════════════════════════════════════ */
  /* HERO — multi-watch reveal                                           */
  /* ══════════════════════════════════════════════════════════════════ */
  function buildHero(container, assets) {
    var W = 1920, H = 1080, K = H / 720;
    var WATCHES = {
      pearl: { src: assets.pearl, w: 1047, h: 2534, fx: 0.4987, fy: 0.531 },
      rosso: { src: assets.rosso, w: 730, h: 1818, fx: 0.4991, fy: 0.5162 },
      india: { src: assets.india, w: 1047, h: 2534, fx: 0.4988, fy: 0.5312 },
      firefly: { src: assets.firefly, w: 1063, h: 2534, fx: 0.5007, fy: 0.5333 }
    };
    var CHAPTERS = [
      { watch: 'pearl', side: 'R', accent: RED, eyebrow: 'Summit — Pearl', lines: ['NO HYPE.', 'JUST BOLD,', 'BEAUTIFUL', 'WATCHES.'], hi: 1 },
      { watch: 'rosso', side: 'L', accent: RED, eyebrow: 'Voyager — Rosso', lines: ['MADE TO', 'STAND OUT.'], hi: 1 },
      { watch: 'india', side: 'R', accent: '#1FA64A', eyebrow: 'Summit — India', lines: ['YOUR STYLE.', 'YOUR RULES.'], hi: 1 },
      { watch: 'firefly', side: 'L', accent: '#F5C518', eyebrow: 'Voyager — Firefly', lines: ['BUILT FOR', 'THE BOLD.'], hi: 1 }
    ];
    var CH_DUR = 3.35, INTRO = 1.5, OUTRO = 2.4;
    var DUR = INTRO + CHAPTERS.length * CH_DUR + OUTRO;

    var S = Stage(container, W, H, DUR);
    S.root.style.background = 'radial-gradient(120% 95% at 55% 42%, #26262b 0%, #131316 44%, #08080a 100%)';
    var master = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, S.root);
    el('div', {
      position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '5',
      boxShadow: 'inset 0 0 ' + (240 * K) + 'px ' + (70 * K) + 'px rgba(0,0,0,0.72)'
    }, master);

    var subStage = { root: master, on: S.on };

    brandCard({ root: master, on: S.on }, { t0: 0, t1: INTRO, mode: 'intro', logo: assets.logo, logoW: 300, K: K, tagline: 'No Hype. Just Bold.' });

    CHAPTERS.forEach(function (cfg, i) {
      var t0 = INTRO + i * CH_DUR - (i === 0 ? 0 : 0.3);
      var t1 = INTRO + (i + 1) * CH_DUR;
      var dur = t1 - t0;
      var sign = cfg.side === 'R' ? 1 : -1;
      var geo = WATCHES[cfg.watch];
      var h = H * 1.20, aspect = geo.w / geo.h, w = h * aspect;
      var cx = W * (cfg.side === 'R' ? 0.68 : 0.32), cy = H * 0.52;

      var layer = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, master);

      var glow = el('div', {
        position: 'absolute', left: (cx - 360 * K) + 'px', top: (cy - 320 * K) + 'px',
        width: (720 * K) + 'px', height: (640 * K) + 'px',
        background: 'radial-gradient(closest-side, ' + cfg.accent + '33, transparent 70%)',
        filter: 'blur(' + (10 * K) + 'px)'
      }, layer);

      var watch = el('div', {
        position: 'absolute', left: (cx - geo.fx * w) + 'px', top: (cy - geo.fy * h) + 'px',
        width: w + 'px', height: h + 'px',
        transformOrigin: (geo.fx * 100) + '% ' + (geo.fy * 100) + '%',
        willChange: 'transform, filter, opacity'
      }, layer);
      img(geo.src, { width: '100%', height: '100%', display: 'block', filter: 'drop-shadow(0 34px 60px rgba(0,0,0,0.55))' }, watch);
      var gloss = el('div', {
        position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', pointerEvents: 'none',
        WebkitMaskImage: 'url(' + geo.src + ')', maskImage: 'url(' + geo.src + ')',
        WebkitMaskSize: '100% 100%', maskSize: '100% 100%', mixBlendMode: 'screen', opacity: '0'
      }, watch);

      var isR = cfg.side === 'R';
      var anchor = isR ? { left: (W * 0.072) + 'px' } : { right: (W * 0.072) + 'px' };
      var blockTop = H * 0.30, lineH = H * 0.108;
      var startY = blockTop + (cfg.lines.length <= 2 ? H * 0.06 : 0);

      var eyebrowRow = el('div', {
        position: 'absolute', top: (startY - H * 0.075) + 'px', display: 'flex',
        alignItems: 'center', gap: (10 * K) + 'px',
        flexDirection: isR ? 'row' : 'row-reverse'
      }, layer);
      for (var a in anchor) eyebrowRow.style[a] = anchor[a];
      el('span', { width: (8 * K) + 'px', height: (8 * K) + 'px', borderRadius: '50%', background: cfg.accent, display: 'inline-block' }, eyebrowRow);
      var eb = el('span', {
        fontFamily: UI, fontWeight: '600', fontSize: (15 * K) + 'px', letterSpacing: '0.32em',
        color: 'rgba(255,255,255,0.66)', textTransform: 'uppercase'
      }, eyebrowRow);
      eb.textContent = cfg.eyebrow;

      var divider = el('div', {
        position: 'absolute', top: (startY - H * 0.028) + 'px', height: (3 * K) + 'px',
        background: cfg.accent, borderRadius: (2 * K) + 'px', width: '0'
      }, layer);
      for (var a2 in anchor) divider.style[a2] = anchor[a2];

      var lines = cfg.lines.map(function (ln, li) {
        var d = el('div', {
          position: 'absolute', top: (startY + li * lineH) + 'px',
          fontFamily: DISP, fontWeight: '800', fontSize: (H * 0.10) + 'px', lineHeight: '0.94',
          letterSpacing: '0.004em', color: li === cfg.hi ? cfg.accent : '#fff',
          textTransform: 'uppercase', textAlign: isR ? 'left' : 'right'
        }, layer);
        for (var a3 in anchor) d.style[a3] = anchor[a3];
        d.textContent = ln;
        return d;
      });

      var vis = rangeLayer(layer, t0, t1, 0.6);
      S.on(function (t) {
        if (!vis(t)) return;
        var L = t - t0;
        var op = Math.min(ramp(L, 0, 0.5, E.outQuad), 1 - ramp(L, dur - 0.55, dur, E.inQuad));
        layer.style.opacity = op;
        var enter = ramp(L, 0, 0.75, E.outCubic);
        glow.style.opacity = enter;
        var settle = lerp(1.05, 1.0, enter);
        var tx = lerp(sign * W * 0.34, 0, enter) - E.inQuad(ramp(L, dur - 0.55, dur, function (x) { return x; })) * sign * W * 0.16;
        watch.style.transform = 'translateX(' + tx + 'px) scale(' + settle + ')';
        watch.style.filter = 'blur(' + lerp(9, 0, ramp(L, 0, 0.6)) + 'px)';
        var glossP = ramp(L, 0.55, 2.0, E.inOutQuad);
        if (glossP > 0 && glossP < 1) {
          var x = lerp(-140, 240, glossP);
          gloss.style.opacity = Math.sin(glossP * Math.PI) * 0.85;
          gloss.style.background = 'linear-gradient(18deg, transparent ' + (x - 24) + '%, rgba(255,255,255,.5) ' + (x - 6) + '%, rgba(255,255,255,.95) ' + x + '%, rgba(255,255,255,.5) ' + (x + 6) + '%, transparent ' + (x + 24) + '%)';
        } else {
          gloss.style.opacity = '0';
        }
        var ebIn = ramp(L, 0.5, 1.0);
        eyebrowRow.style.opacity = ebIn;
        eyebrowRow.style.transform = 'translateX(' + lerp(sign * 16 * K, 0, ebIn) + 'px)';
        divider.style.width = lerp(0, 58 * K, ramp(L, 0.6, 1.1)) + 'px';
        lines.forEach(function (d, li) {
          d.style.opacity = ramp(L, 0.7 + li * 0.13, 1.2 + li * 0.13);
          d.style.transform = 'translateY(' + lerp(24 * K, 0, ramp(L, 0.7 + li * 0.13, 1.35 + li * 0.13, E.outQuart)) + 'px)';
        });
      });
    });

    brandCard({ root: master, on: S.on }, { t0: DUR - OUTRO, t1: DUR, mode: 'outro', logo: assets.logo, logoW: 300, K: K, outroText: 'Shop the Collection' });

    var corner = img(assets.logo, {
      position: 'absolute', top: (H * 0.075) + 'px', left: (W * 0.055) + 'px',
      width: (118 * K) + 'px', zIndex: '6', opacity: '0'
    }, master);
    S.on(function (t) {
      var o = (t > INTRO - 0.2 && t < DUR - OUTRO + 0.2)
        ? ramp(t, INTRO - 0.2, INTRO + 0.4) * (1 - ramp(t, DUR - OUTRO - 0.3, DUR - OUTRO + 0.2)) : 0;
      corner.style.opacity = o;
    });

    S.on(function (t) { master.style.opacity = loopFade(t, DUR); });
    S.start(INTRO + 1.6);
  }

  /* ══════════════════════════════════════════════════════════════════ */
  /* EXPLAINER — anatomy tour (CH-1 and BR-1 variants share the engine)  */
  /* ══════════════════════════════════════════════════════════════════ */
  function buildExplainer(container, assets, variant) {
    var isBR = variant === 'br';
    var W = 1920, H = 1080, K = H / 720;
    var GEO = isBR
      ? { src: assets.brPearl, w: 1440, h: 1800, fx: 0.472, fy: 0.505 }
      : { src: assets.firefly, w: 1063, h: 2534, fx: 0.5007, fy: 0.5333 };
    var WATCH_H = isBR ? 1480 : 2000, AW = WATCH_H * (GEO.w / GEO.h);
    var CX = W * 0.5, CY = H * 0.5;
    function px(ax) { return CX + (ax - GEO.fx) * AW; }
    function py(ay) { return CY + (ay - GEO.fy) * WATCH_H; }

    var FEATURES = isBR ? [
      { title: 'BRUSHED\nCASE', side: 'L', a: [0.23, 0.63], r: 0.185,
        desc: '316L surgical-grade steel with a masculine brushed finish, pressure-tested to 100 m.',
        chips: ['45 mm', 'Brushed 316L', '10 ATM'] },
      { title: 'THE BEZEL', side: 'R', a: [0.47, 0.30], r: 0.22,
        desc: 'A unidirectional rotating bezel — as functional as it is refined, machined from brushed steel.' },
      { title: 'SAPPHIRE\nCRYSTAL', side: 'L', a: [0.40, 0.44], r: 0.17,
        desc: 'Scratch-resistant, anti-reflective sapphire. The only thing harder is a diamond.' },
      { title: 'THE MOVEMENT', side: 'R', a: [0.47, 0.51], r: 0.23,
        desc: 'The legendary Miyota OS20 beats at 32,768 Hz — among the most robust movements made.',
        chips: ['Miyota OS20', '32,768 Hz'] },
      { title: 'THE DIAL', side: 'L', a: [0.47, 0.47], r: 0.21,
        desc: 'A two-layer "race-track" dial inspired by historical chronographs, glowing up to nine hours in the dark.' },
      { title: 'SUB-DIALS', side: 'R', a: [0.47, 0.60], r: 0.17,
        desc: 'Three sub-dials engineered into the face: 12-hour time, date, and sec / min / hour.' },
      { title: 'CROWN &\nPUSHERS', side: 'R', a: [0.75, 0.51], r: 0.13,
        desc: 'A push-style crown — no screw-down needed — flanked by twin chronograph pushers.' }
    ] : [
      { title: 'THE CASE', side: 'L', a: [0.15, 0.57], r: 0.20,
        desc: '316L surgical-grade stainless steel, precision-machined and rigorously pressure-tested.',
        chips: ['45 mm', '316L Steel', '10 ATM'] },
      { title: 'THE BEZEL', side: 'R', a: [0.50, 0.40], r: 0.24,
        desc: 'A unidirectional rotating bezel — as functional as it is refined, machined from the same steel.' },
      { title: 'SAPPHIRE\nCRYSTAL', side: 'L', a: [0.40, 0.45], r: 0.19,
        desc: 'Scratch-resistant, anti-reflective sapphire. The only thing harder is a diamond.' },
      { title: 'THE MOVEMENT', side: 'R', a: [0.50, 0.535], r: 0.24,
        desc: 'The legendary Miyota OS20 beats at 32,768 Hz — among the most robust movements made.',
        chips: ['Miyota OS20', '32,768 Hz'] },
      { title: 'THE DIAL', side: 'L', a: [0.50, 0.475], r: 0.22,
        desc: 'A two-layer "race-track" dial inspired by historical chronographs, glowing up to nine hours in the dark.' },
      { title: 'SUB-DIALS', side: 'R', a: [0.455, 0.605], r: 0.20,
        desc: 'Three sub-dials engineered into the face: 12-hour time, date, and sec / min / hour.' },
      { title: 'CROWN &\nPUSHERS', side: 'R', a: [0.86, 0.535], r: 0.16,
        desc: 'Chronograph pushers flank a signed, screw-down crown for a watertight seal.' }
    ];
    var BANDS_META = [
      { img: assets.bands.blacksport, name: 'BLACK SPORT', mat: 'Natural Rubber', sw: '#141414' },
      { img: assets.bands.whitesport, name: 'WHITE SPORT', mat: 'Natural Rubber', sw: '#f2f2ef' },
      { img: assets.bands.dkleather, name: 'DARK LEATHER', mat: 'Italian Leather', sw: '#4a2e20' },
      { img: assets.bands.ltleather, name: 'TAN LEATHER', mat: 'Italian Leather', sw: '#b07a45' }
    ].concat(isBR ? [] : [
      { img: assets.bands.blkleather, name: 'BLACK LEATHER', mat: 'Italian Leather', sw: '#1b1b1b' }
    ]).concat([
      { img: assets.bands.hybrid, name: 'HYBRID', mat: 'Leather & Canvas', sw: '#262626' },
      { img: assets.bands.nato, name: 'DOUBLE PASS', mat: 'Woven Canvas', sw: '#111111' },
      { img: assets.bands.metal, name: 'STEEL', mat: '316L Bracelet', sw: '#b9b9bd' }
    ]);
    var COLORS_META = isBR ? [
      { key: 'pearl', name: 'PEARL', accent: '#E8112D', sub: 'Silver · Red', geo: { src: assets.brPearl, w: 1440, h: 1800, fx: 0.472, fy: 0.505 } },
      { key: 'india', name: 'INDIA', accent: '#1FA64A', sub: 'Emerald · Green', geo: { src: assets.brIndia, w: 1440, h: 1800, fx: 0.472, fy: 0.505 } }
    ] : [
      { key: 'firefly', name: 'FIREFLY', accent: '#F5C518', sub: 'Obsidian · Yellow', geo: { src: assets.firefly, w: 1063, h: 2534, fx: 0.5007, fy: 0.5333 } },
      { key: 'pearl', name: 'PEARL', accent: '#E8112D', sub: 'Silver · Red', geo: { src: assets.pearl, w: 1047, h: 2534, fx: 0.4987, fy: 0.531 } },
      { key: 'rosso', name: 'ROSSO', accent: '#E8112D', sub: 'Scarlet · Steel', geo: { src: assets.rosso, w: 730, h: 1818, fx: 0.4991, fy: 0.5162 } },
      { key: 'india', name: 'INDIA', accent: '#1FA64A', sub: 'Emerald · Steel', geo: { src: assets.india, w: 1047, h: 2534, fx: 0.4988, fy: 0.5312 } }
    ];
    var STRAP_MM = isBR ? '24 mm Quick-Release' : '22 mm Quick-Release';
    var SPECS = [
      ['Case Material', isBR ? '316L Brushed Steel' : '316L Stainless Steel'],
      ['Case Diameter', '45 mm'],
      ['Movement', 'Miyota OS20 (Japan)'],
      ['Crystal', 'Sapphire, AR-Coated'],
      ['Water Resistance', '100 m / 10 ATM'],
      ['Strap', STRAP_MM]
    ];
    var INTRO_BIG = isBR ? 'SUMMIT CHRONOGRAPH' : 'VOYAGER CHRONOGRAPH';
    var COLORS_EYEBROW = isBR ? 'Available in Multiple Colorways' : 'Available in Four Colorways';

    var INTRO = 2.2, FEAT = 5.2,
        BANDS_DUR = isBR ? 10.0 : 11.4,
        COLORS_DUR = isBR ? 5.4 : 8.6,
        SPECS_DUR = 3.4, OUTRO = 2.6;
    var FEAT_START = INTRO;
    var FEAT_END = FEAT_START + FEATURES.length * FEAT;
    var BANDS_START = FEAT_END;
    var COLORS_START = BANDS_START + BANDS_DUR;
    var SPECS_START = COLORS_START + COLORS_DUR;
    var OUTRO_START = SPECS_START + SPECS_DUR;
    var DUR = OUTRO_START + OUTRO;

    var S = Stage(container, W, H, DUR);
    S.root.style.background = 'radial-gradient(120% 95% at 50% 40%, #26262b 0%, #131316 46%, #08080a 100%)';
    var master = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, S.root);
    el('div', {
      position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', zIndex: '5', pointerEvents: 'none',
      boxShadow: 'inset 0 0 ' + (240 * K) + 'px ' + (64 * K) + 'px rgba(0,0,0,0.6)'
    }, master);

    brandCard({ root: master, on: S.on }, { t0: 0, t1: INTRO, mode: 'intro', logo: assets.logo, logoW: 260, K: K, big: INTRO_BIG, tagline: 'A Guided Tour' });

    /* the hero watch, still, during the feature tour */
    var watch = el('div', {
      position: 'absolute', left: (CX - GEO.fx * AW) + 'px', top: (CY - GEO.fy * WATCH_H) + 'px',
      width: AW + 'px', height: WATCH_H + 'px',
      transformOrigin: (GEO.fx * 100) + '% ' + (GEO.fy * 100) + '%',
      willChange: 'transform, opacity', opacity: '0'
    }, master);
    img(GEO.src, { width: '100%', height: '100%', display: 'block', filter: 'drop-shadow(0 40px 70px rgba(0,0,0,0.6))' }, watch);
    S.on(function (t) {
      var wIn = ramp(t, INTRO - 0.7, INTRO + 0.3, E.outCubic);
      var wOut = ramp(t, FEAT_END - 0.6, FEAT_END - 0.05, E.inQuad);
      var wOp = Math.min(wIn, 1 - wOut);
      watch.style.display = wOp > 0.001 ? '' : 'none';
      watch.style.opacity = wOp;
      watch.style.transform = 'scale(' + lerp(1.05, 1.0, ramp(t, INTRO - 0.7, INTRO + 0.9, E.outCubic)) + ')';
    });

    /* feature callouts */
    FEATURES.forEach(function (cfg, i) {
      var t0 = FEAT_START + i * FEAT + (i === 0 ? 0.1 : 0);
      var t1 = FEAT_START + (i + 1) * FEAT;
      var dur = t1 - t0;
      var ax = px(cfg.a[0]), ay = py(cfg.a[1]);
      var R = cfg.r * AW;
      var side = cfg.side, isR = side === 'R';
      var labelY = Math.max(H * 0.30, Math.min(H * 0.74, ay));
      var attachX = isR ? W * 0.735 : W * 0.265;

      var layer = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, master);
      var spot = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '2' }, layer);

      var svg = svgEl('svg', { width: W, height: H, viewBox: '0 0 ' + W + ' ' + H }, layer);
      svg.style.position = 'absolute'; svg.style.left = '0'; svg.style.top = '0'; svg.style.zIndex = '3'; svg.style.pointerEvents = 'none';
      // theme CSS force-sizes svgs (icons); inline sizing must win
      svg.style.width = W + 'px'; svg.style.height = H + 'px'; svg.style.maxWidth = 'none'; svg.style.maxHeight = 'none';
      // dark halos first, so the white ring/line stay readable on bright steel
      var ringHalo = svgEl('circle', { cx: ax, cy: ay, fill: 'none', stroke: 'rgba(0,0,0,0.45)', 'stroke-width': 5.5 * K }, svg);
      var ring = svgEl('circle', { cx: ax, cy: ay, fill: 'none', stroke: 'rgba(255,255,255,0.95)', 'stroke-width': 2.4 * K }, svg);
      var ping = svgEl('circle', { cx: ax, cy: ay, fill: 'none', stroke: RED, 'stroke-width': 1.6 * K }, svg);
      var lineHalo = svgEl('line', { stroke: 'rgba(0,0,0,0.5)', 'stroke-width': 4.5 * K }, svg);
      var line = svgEl('line', { stroke: 'rgba(255,255,255,0.9)', 'stroke-width': 1.8 * K }, svg);
      var dotA = svgEl('circle', { r: 4 * K, fill: RED }, svg);
      var dotB = svgEl('circle', { r: 3 * K, fill: 'rgba(255,255,255,0.9)' }, svg);

      var label = el('div', {
        position: 'absolute', top: labelY + 'px', width: (W * 0.225) + 'px', zIndex: '4',
        display: 'flex', flexDirection: 'column', alignItems: isR ? 'flex-start' : 'flex-end',
        textAlign: isR ? 'left' : 'right'
      }, layer);
      label.style[isR ? 'left' : 'right'] = (W * 0.75) + 'px';

      var ebRow = el('div', {
        display: 'flex', alignItems: 'center', flexDirection: isR ? 'row' : 'row-reverse',
        gap: (12 * K) + 'px', marginBottom: (14 * K) + 'px'
      }, label);
      var num = el('span', { fontFamily: DISP, fontWeight: '800', fontSize: (34 * K) + 'px', color: RED, lineHeight: '1' }, ebRow);
      num.textContent = String(i + 1).padStart(2, '0');
      var feat = el('span', {
        fontFamily: UI, fontWeight: '600', fontSize: (13 * K) + 'px', letterSpacing: '0.34em',
        color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase'
      }, ebRow);
      feat.textContent = 'Feature / ' + String(FEATURES.length).padStart(2, '0');

      var title = el('div', {
        fontFamily: DISP, fontWeight: '800', fontSize: (60 * K) + 'px', lineHeight: '0.92',
        letterSpacing: '0.01em', color: '#fff', textTransform: 'uppercase', whiteSpace: 'pre-line'
      }, label);
      title.textContent = cfg.title;

      var div2 = el('div', { height: (3 * K) + 'px', background: RED, borderRadius: '2px', margin: (18 * K) + 'px 0', width: '0' }, label);

      var desc = el('div', {
        fontFamily: UI, fontWeight: '400', fontSize: (20 * K) + 'px', lineHeight: '1.42',
        color: 'rgba(255,255,255,0.74)', maxWidth: (460 * K) + 'px'
      }, label);
      desc.textContent = cfg.desc;

      var chipsRow = null;
      if (cfg.chips) {
        chipsRow = el('div', {
          display: 'flex', flexWrap: 'wrap', gap: (8 * K) + 'px', marginTop: (20 * K) + 'px',
          justifyContent: isR ? 'flex-start' : 'flex-end'
        }, label);
        cfg.chips.forEach(function (c) {
          var chip = el('span', {
            fontFamily: UI, fontWeight: '600', fontSize: (13 * K) + 'px', letterSpacing: '0.08em',
            color: 'rgba(255,255,255,0.82)', textTransform: 'uppercase',
            padding: (7 * K) + 'px ' + (13 * K) + 'px', border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: '100px', background: 'rgba(255,255,255,0.04)'
          }, chipsRow);
          chip.textContent = c;
        });
      }

      var vis = rangeLayer(layer, t0, t1, 0.4);
      S.on(function (t) {
        if (!vis(t)) return;
        var L = t - t0;
        var op = Math.min(ramp(L, 0, 0.5, E.outQuad), 1 - ramp(L, dur - 0.5, dur, E.inQuad));
        var enter = ramp(L, 0.05, 0.7, E.outBack);
        var ringR = Math.max(1, R * lerp(1.28, 1.0, enter));
        spot.style.background = 'radial-gradient(circle ' + (R * 2.1) + 'px at ' + ax + 'px ' + ay + 'px, rgba(6,6,8,0) ' + (R * 0.62) + 'px, rgba(6,6,8,' + (0.66 * op) + ') ' + (R * 1.35) + 'px)';
        svg.style.opacity = op;
        ring.setAttribute('r', ringR);
        ring.setAttribute('opacity', Math.max(0, enter));
        ringHalo.setAttribute('r', ringR);
        ringHalo.setAttribute('opacity', Math.max(0, enter) * 0.8);
        var pcy = (L * 0.7) % 1;
        ping.setAttribute('r', ringR * (1 + 0.16 * pcy));
        ping.setAttribute('opacity', Math.max(0, (1 - pcy) * 0.6 * enter));
        var dx = attachX - ax, dy = labelY - ay;
        var len = Math.hypot(dx, dy) || 1;
        var ex = ax + (dx / len) * ringR, ey = ay + (dy / len) * ringR;
        var draw = ramp(L, 0.22, 0.75, E.inOutCubic);
        var lx = ex + (attachX - ex) * draw, ly = ey + (labelY - ey) * draw;
        line.setAttribute('x1', ex); line.setAttribute('y1', ey);
        line.setAttribute('x2', lx); line.setAttribute('y2', ly);
        lineHalo.setAttribute('x1', ex); lineHalo.setAttribute('y1', ey);
        lineHalo.setAttribute('x2', lx); lineHalo.setAttribute('y2', ly);
        lineHalo.setAttribute('opacity', draw * 0.8);
        dotA.setAttribute('cx', ex); dotA.setAttribute('cy', ey); dotA.setAttribute('opacity', draw);
        dotB.setAttribute('cx', lx); dotB.setAttribute('cy', ly); dotB.setAttribute('opacity', draw);
        var labelIn = ramp(L, 0.5, 1.05, E.outCubic);
        label.style.opacity = op;
        label.style.transform = 'translateY(-50%) translateX(' + lerp((isR ? 20 : -20) * K, 0, labelIn) + 'px)';
        ebRow.style.opacity = ramp(L, 0.5, 1.0);
        title.style.opacity = labelIn;
        div2.style.width = lerp(0, 54 * K, ramp(L, 0.7, 1.2)) + 'px';
        desc.style.opacity = ramp(L, 0.85, 1.4);
        if (chipsRow) chipsRow.style.opacity = ramp(L, 1.0, 1.5);
      });
    });

    /* bands cycle */
    (function () {
      var t0 = BANDS_START, t1 = COLORS_START, dur = t1 - t0;
      var lead = 0.55, SLOT = 1.25, XF = 0.42, N = BANDS_META.length;
      var layer = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, master);
      var TX = W * 0.085, TY = H * 0.5;
      var cardH = H * 0.8, cardW = cardH * (780 / 1220);
      var cardX = W * 0.545, cardY = (H - cardH) / 2;

      var card = el('div', { position: 'absolute', left: cardX + 'px', top: cardY + 'px', width: cardW + 'px', height: cardH + 'px' }, layer);
      var bandImgs = BANDS_META.map(function (b) {
        return img(b.img, {
          position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', width: '100%', height: '100%', display: 'block',
          objectFit: 'contain', opacity: '0', filter: 'drop-shadow(0 34px 60px rgba(0,0,0,0.6))'
        }, card);
      });

      var text = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, layer);
      var ebRow = el('div', { position: 'absolute', left: TX + 'px', top: (TY - 196 * K) + 'px', display: 'flex', alignItems: 'center', gap: (12 * K) + 'px' }, text);
      el('span', { width: (40 * K) + 'px', height: (3 * K) + 'px', background: RED, borderRadius: '2px' }, ebRow);
      var ebT = el('span', { fontFamily: UI, fontWeight: '600', fontSize: (14 * K) + 'px', letterSpacing: '0.34em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase' }, ebRow);
      ebT.textContent = STRAP_MM;
      var sub = el('div', { position: 'absolute', left: TX + 'px', top: (TY - 156 * K) + 'px', fontFamily: UI, fontWeight: '600', fontSize: (15 * K) + 'px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }, text);
      sub.textContent = 'Make It Yours';
      var nameWrap = el('div', { position: 'absolute', left: TX + 'px', top: (TY - 100 * K) + 'px', width: (W * 0.42) + 'px', height: (120 * K) + 'px' }, text);
      var nameEls = BANDS_META.map(function (b) {
        var d = el('div', { position: 'absolute', left: '0', top: '0', fontFamily: DISP, fontWeight: '800', fontSize: (88 * K) + 'px', lineHeight: '0.9', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.01em', opacity: '0' }, nameWrap);
        d.textContent = b.name; return d;
      });
      var matWrap = el('div', { position: 'absolute', left: TX + 'px', top: (TY + 34 * K) + 'px', width: (W * 0.42) + 'px', height: (44 * K) + 'px' }, text);
      var matEls = BANDS_META.map(function (b) {
        var d = el('div', { position: 'absolute', left: '0', top: '0', display: 'flex', alignItems: 'center', gap: (12 * K) + 'px', opacity: '0' }, matWrap);
        el('span', { width: (20 * K) + 'px', height: (20 * K) + 'px', borderRadius: '50%', background: b.sw, border: '1px solid rgba(255,255,255,0.35)' }, d);
        var s2 = el('span', { fontFamily: UI, fontWeight: '500', fontSize: (24 * K) + 'px', color: 'rgba(255,255,255,0.68)' }, d);
        s2.textContent = b.mat; return d;
      });
      var ticksRow = el('div', { position: 'absolute', left: TX + 'px', top: (TY + 108 * K) + 'px', display: 'flex', gap: (10 * K) + 'px', alignItems: 'flex-end' }, text);
      var ticks = BANDS_META.map(function () {
        return el('div', { width: (5 * K) + 'px', height: (16 * K) + 'px', borderRadius: '3px', background: 'rgba(255,255,255,0.28)' }, ticksRow);
      });
      var counter = el('div', { position: 'absolute', left: TX + 'px', top: (TY + 158 * K) + 'px', fontFamily: UI, fontWeight: '600', fontSize: (14 * K) + 'px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.4)' }, text);

      var vis = rangeLayer(layer, t0, t1, 0.3);
      S.on(function (t) {
        if (!vis(t)) return;
        var L = t - t0;
        var secOp = Math.min(ramp(L, 0, 0.55), 1 - ramp(L, dur - 0.6, dur, E.inQuad));
        var idx = Math.max(0, Math.min(N - 1, Math.floor((L - lead) / SLOT)));
        function opFor(i2) {
          var s = lead + i2 * SLOT;
          var inO = smooth((L - (s - XF / 2)) / XF);
          var outO = i2 === N - 1 ? 0 : smooth((L - (lead + (i2 + 1) * SLOT - XF / 2)) / XF);
          return inO * (1 - outO);
        }
        var cardIn = ramp(L, 0.1, 0.85, E.outCubic);
        card.style.opacity = secOp * cardIn;
        card.style.transform = 'translateX(' + lerp(46 * K, 0, cardIn) + 'px)';
        var textIn = ramp(L, 0.25, 0.95, E.outCubic);
        text.style.opacity = secOp;
        ebRow.style.opacity = textIn; sub.style.opacity = textIn;
        ticksRow.style.opacity = textIn; counter.style.opacity = textIn;
        for (var i2 = 0; i2 < N; i2++) {
          var o = opFor(i2);
          bandImgs[i2].style.opacity = o;
          nameEls[i2].style.opacity = o;
          matEls[i2].style.opacity = o;
          ticks[i2].style.height = ((i2 === idx ? 30 : 16) * K) + 'px';
          ticks[i2].style.background = i2 === idx ? RED : 'rgba(255,255,255,0.28)';
        }
        counter.textContent = String(idx + 1).padStart(2, '0') + ' / ' + String(N).padStart(2, '0');
      });
    })();

    /* colorways cycle */
    (function () {
      var t0 = COLORS_START, t1 = SPECS_START, dur = t1 - t0;
      var lead = 0.55, SLOT = 1.9, XF = 0.5, N = COLORS_META.length;
      var layer = el('div', { position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%' }, master);
      var nameY = H * 0.73;
      var watchEls = COLORS_META.map(function (m) {
        var g = m.geo, w2 = WATCH_H * (g.w / g.h);
        var d = el('div', { position: 'absolute', left: (CX - g.fx * w2) + 'px', top: (CY - g.fy * WATCH_H) + 'px', width: w2 + 'px', height: WATCH_H + 'px', opacity: '0' }, layer);
        img(g.src, { width: '100%', height: '100%', display: 'block', filter: 'drop-shadow(0 40px 70px rgba(0,0,0,0.6))' }, d);
        return d;
      });
      el('div', { position: 'absolute', left: '0', right: '0', bottom: '0', height: (H * 0.36) + 'px', background: 'linear-gradient(to top, rgba(6,6,8,0.92), rgba(6,6,8,0))' }, layer);
      el('div', { position: 'absolute', left: '0', right: '0', top: '0', height: (H * 0.22) + 'px', background: 'linear-gradient(to bottom, rgba(6,6,8,0.88), rgba(6,6,8,0))' }, layer);
      var eyebrow = el('div', { position: 'absolute', top: (H * 0.1) + 'px', left: '0', width: W + 'px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: (14 * K) + 'px' }, layer);
      el('span', { width: (40 * K) + 'px', height: (3 * K) + 'px', background: RED, borderRadius: '2px' }, eyebrow);
      var ebT2 = el('span', { fontFamily: UI, fontWeight: '600', fontSize: (15 * K) + 'px', letterSpacing: '0.4em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }, eyebrow);
      ebT2.textContent = COLORS_EYEBROW;
      el('span', { width: (40 * K) + 'px', height: (3 * K) + 'px', background: RED, borderRadius: '2px' }, eyebrow);
      var dotsRow = el('div', { position: 'absolute', left: '0', top: (nameY - 52 * K) + 'px', width: W + 'px', display: 'flex', justifyContent: 'center', gap: (12 * K) + 'px' }, layer);
      var dots = COLORS_META.map(function () {
        return el('div', { width: (9 * K) + 'px', height: (9 * K) + 'px', borderRadius: '100px', background: 'rgba(255,255,255,0.3)' }, dotsRow);
      });
      var nameEls = COLORS_META.map(function (m) {
        var d = el('div', { position: 'absolute', left: '0', top: nameY + 'px', width: W + 'px', textAlign: 'center', opacity: '0' }, layer);
        var n1 = el('div', { fontFamily: DISP, fontWeight: '800', fontSize: (84 * K) + 'px', lineHeight: '0.9', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.02em' }, d);
        n1.textContent = m.name;
        var n2 = el('div', { fontFamily: UI, fontWeight: '600', fontSize: (18 * K) + 'px', letterSpacing: '0.3em', color: m.accent, textTransform: 'uppercase', marginTop: (14 * K) + 'px' }, d);
        n2.textContent = m.sub;
        return d;
      });
      var vis = rangeLayer(layer, t0, t1, 0.3);
      S.on(function (t) {
        if (!vis(t)) return;
        var L = t - t0;
        var secOp = Math.min(ramp(L, 0, 0.55), 1 - ramp(L, dur - 0.6, dur, E.inQuad));
        layer.style.opacity = secOp;
        var idx = Math.max(0, Math.min(N - 1, Math.floor((L - lead) / SLOT)));
        function opFor(i2) {
          var s = lead + i2 * SLOT;
          var inO = smooth((L - (s - XF / 2)) / XF);
          var outO = i2 === N - 1 ? 0 : smooth((L - (lead + (i2 + 1) * SLOT - XF / 2)) / XF);
          return inO * (1 - outO);
        }
        var hdr = ramp(L, 0.2, 0.9);
        eyebrow.style.opacity = hdr; dotsRow.style.opacity = hdr;
        for (var i2 = 0; i2 < N; i2++) {
          var o = opFor(i2);
          watchEls[i2].style.opacity = o;
          nameEls[i2].style.opacity = o;
          dots[i2].style.width = ((i2 === idx ? 30 : 9) * K) + 'px';
          dots[i2].style.background = i2 === idx ? COLORS_META[i2].accent : 'rgba(255,255,255,0.3)';
        }
      });
    })();

    /* specs card */
    (function () {
      var t0 = SPECS_START, t1 = OUTRO_START, dur = t1 - t0;
      var layer = el('div', {
        position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', width: '100%', height: '100%', zIndex: '6', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }, master);
      var head = el('div', { display: 'flex', alignItems: 'center', gap: (14 * K) + 'px', marginBottom: (46 * K) + 'px' }, layer);
      el('span', { width: (40 * K) + 'px', height: (3 * K) + 'px', background: RED, borderRadius: '2px' }, head);
      var ht = el('span', { fontFamily: UI, fontWeight: '600', fontSize: (16 * K) + 'px', letterSpacing: '0.4em', color: 'rgba(255,255,255,0.62)', textTransform: 'uppercase' }, head);
      ht.textContent = 'Technical Specifications';
      el('span', { width: (40 * K) + 'px', height: (3 * K) + 'px', background: RED, borderRadius: '2px' }, head);
      var grid = el('div', { display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: (120 * K) + 'px', rowGap: (30 * K) + 'px', width: (W * 0.62) + 'px' }, layer);
      var rows = SPECS.map(function (kv) {
        var d = el('div', { display: 'flex', flexDirection: 'column', gap: (7 * K) + 'px', borderBottom: '1px solid rgba(255,255,255,0.12)', paddingBottom: (16 * K) + 'px', opacity: '0' }, grid);
        var kEl = el('span', { fontFamily: UI, fontWeight: '600', fontSize: (13 * K) + 'px', letterSpacing: '0.22em', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase' }, d);
        kEl.textContent = kv[0];
        var vEl = el('span', { fontFamily: DISP, fontWeight: '700', fontSize: (40 * K) + 'px', color: '#fff', letterSpacing: '0.01em' }, d);
        vEl.textContent = kv[1];
        return d;
      });
      var vis = rangeLayer(layer, t0, t1, 0.3);
      S.on(function (t) {
        if (!vis(t)) return;
        var L = t - t0;
        layer.style.opacity = Math.min(ramp(L, 0, 0.5), 1 - ramp(L, dur - 0.5, dur, E.inQuad));
        head.style.opacity = ramp(L, 0.2, 0.8);
        rows.forEach(function (d, i) {
          d.style.opacity = ramp(L, 0.4 + i * 0.08, 1.0 + i * 0.08);
          d.style.transform = 'translateY(' + lerp(18 * K, 0, ramp(L, 0.4 + i * 0.08, 1.1 + i * 0.08, E.outQuart)) + 'px)';
        });
      });
    })();

    brandCard({ root: master, on: S.on }, { t0: OUTRO_START, t1: DUR, mode: 'outro', logo: assets.logo, logoW: 260, K: K, big: 'ENGINEERED TO BE WORN', outroText: 'Shop the Collection' });

    var corner = img(assets.logo, {
      position: 'absolute', top: (H * 0.07) + 'px', left: (W * 0.05) + 'px',
      width: (110 * K) + 'px', zIndex: '7', opacity: '0'
    }, master);
    S.on(function (t) {
      corner.style.opacity = ramp(t, INTRO - 0.2, INTRO + 0.4) * (1 - ramp(t, SPECS_START - 0.5, SPECS_START - 0.05));
    });

    S.on(function (t) { master.style.opacity = loopFade(t, DUR); });
    S.start(FEAT_START + 2.2);
  }

  /* ── boot: lazy-init each declared animation ───────────────────────── */
  function initOne(node) {
    if (node.getAttribute('data-luca-anim-built')) return;
    var cfgEl = node.querySelector('script[type="application/json"]');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    node.setAttribute('data-luca-anim-built', '1');
    var kind = node.getAttribute('data-luca-anim');
    if (kind === 'hero') buildHero(node, cfg.assets);
    else if (kind === 'explainer') buildExplainer(node, cfg.assets, 'ch');
    else if (kind === 'explainer-br') buildExplainer(node, cfg.assets, 'br');
  }
  function boot() {
    var nodes = document.querySelectorAll('[data-luca-anim]');
    if (!nodes.length) return;
    if ('IntersectionObserver' in window) {
      var ioAlive = false;
      var io = new IntersectionObserver(function (entries) {
        ioAlive = true;
        entries.forEach(function (en) {
          if (en.isIntersecting) { io.unobserve(en.target); initOne(en.target); }
        });
      }, { rootMargin: '600px 0px' });
      nodes.forEach(function (n) { io.observe(n); });
      // Safety net: only if the observer never delivers a single callback
      // (stalled embedded browsers) build everything anyway. Idempotent.
      setTimeout(function () { if (!ioAlive) nodes.forEach(initOne); }, 5000);
    } else {
      nodes.forEach(initOne);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
